import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, ExternalLink, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { listBriefRequests, getBriefUrl, type BriefRequest } from '@/lib/brief-api';
import { pipelineFetch } from '@/lib/pipeline-auth';

interface ChatMessage {
  id: string;
  role: 'user' | 'system';
  content: string;
  briefId?: string;
  briefReady?: boolean;
}

export default function BriefRequests() {
  const { toast } = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [requests, setRequests] = useState<BriefRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [pollingBriefIds, setPollingBriefIds] = useState<Set<string>>(new Set());
  const [pollingStartTimes, setPollingStartTimes] = useState<Map<string, number>>(new Map());
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const POLLING_TIMEOUT = 5 * 60 * 1000; // 5 minutes

  const fetchRequests = useCallback(async () => {
    try {
      const data = await listBriefRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to fetch brief requests', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // Poll when we have pending brief IDs
  useEffect(() => {
    if (pollingBriefIds.size === 0) return;
    const interval = setInterval(async () => {
      try {
        const data = await listBriefRequests();
        const arr = Array.isArray(data) ? data : [];
        setRequests(arr);
        const readyIds = new Set<string>();
        const timedOutIds = new Set<string>();
        const now = Date.now();
        
        pollingBriefIds.forEach((id) => {
          const startTime = pollingStartTimes.get(id);
          
          // Check for timeout
          if (startTime && now - startTime > POLLING_TIMEOUT) {
            timedOutIds.add(id);
            return;
          }
          
          const found = arr.find((r) => r.id === id);
          if (found?.status === 'ready') readyIds.add(id);
        });
        
        // Handle timeouts
        if (timedOutIds.size > 0) {
          setPollingBriefIds((prev) => {
            const next = new Set(prev);
            timedOutIds.forEach((id) => next.delete(id));
            return next;
          });
          setPollingStartTimes((prev) => {
            const next = new Map(prev);
            timedOutIds.forEach((id) => next.delete(id));
            return next;
          });
          timedOutIds.forEach(() => {
            setMessages((prev) => [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: 'system',
                content: 'Brief generation timed out. Please try again.',
              },
            ]);
            toast({ 
              title: 'Timeout', 
              description: 'Brief generation took too long. Please try again.', 
              variant: 'destructive' 
            });
          });
        }
        
        // Handle ready briefs
        if (readyIds.size > 0) {
          setPollingBriefIds((prev) => {
            const next = new Set(prev);
            readyIds.forEach((id) => next.delete(id));
            return next;
          });
          setPollingStartTimes((prev) => {
            const next = new Map(prev);
            readyIds.forEach((id) => next.delete(id));
            return next;
          });
          readyIds.forEach((id) => {
            setMessages((prev) => [
              ...prev,
              {
                id: crypto.randomUUID(),
                role: 'system',
                content: 'Brief ready! View Brief ↗',
                briefId: id,
                briefReady: true,
              },
            ]);
          });
        }
      } catch (e) {
        console.error('Polling error', e);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [pollingBriefIds, pollingStartTimes, POLLING_TIMEOUT, toast]);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    setSending(true);

    try {
      const res = await pipelineFetch('/api/chat-brief', {
        method: 'POST',
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok) throw new Error(`Error: ${res.statusText}`);
      const data = await res.json();

      const sysMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'system',
        content: data.message || `Generating brief for ${data.brand_name || 'your request'}...`,
        briefId: data.id || data.request_id,
      };
      setMessages((prev) => [...prev, sysMsg]);

      if (sysMsg.briefId) {
        setPollingBriefIds((prev) => new Set(prev).add(sysMsg.briefId!));
        setPollingStartTimes((prev) => new Map(prev).set(sysMsg.briefId!, Date.now()));
      }
      await fetchRequests();
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'system', content: `Error: ${e.message}` },
      ]);
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  return (
    // gap-6 on the column matches the space-y-6 rhythm other pages get from
    // their wrapper, and keeps the h1 itself margin-free.
    <div className="flex flex-col gap-6 h-[calc(100vh-6rem)]">
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Brief Requests</h1>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 flex-1 min-h-0">
        {/* Left: Chat */}
        <Card className="lg:col-span-3 flex flex-col min-h-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Chat</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col flex-1 min-h-0 gap-3">
            <ScrollArea className="flex-1">
              <div className="space-y-3 pr-3">
                {messages.length === 0 && (
                  <p className="text-muted-foreground text-sm text-center py-12">
                    Describe the brief you need and we'll generate it for you.
                  </p>
                )}
                {messages.map((msg) => (
                  <ChatBubble key={msg.id} message={msg} />
                ))}
                <div ref={scrollRef} />
              </div>
            </ScrollArea>
            <div className="flex gap-2 items-end">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  const el = e.target;
                  el.style.height = 'auto';
                  el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Make me a brief for Nello SuperCalm — it's a stress supplement for women. Competitor is Goli."
                disabled={sending}
                rows={1}
                className="min-h-[40px] max-h-[200px] resize-none overflow-y-auto"
              />
              <Button
                type="button"
                size="icon"
                className="shrink-0"
                disabled={sending || !input.trim()}
                onClick={handleSend}
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Right: History */}
        <Card className="lg:col-span-2 flex flex-col min-h-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Brief History</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 min-h-0">
            <ScrollArea className="h-full">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : requests.length === 0 ? (
                <p className="text-muted-foreground text-sm">No briefs yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Brand</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((req) => (
                      <TableRow
                        key={req.id}
                        className="cursor-pointer"
                        onClick={() => {
                          if (req.status === 'ready') window.open(getBriefUrl(req.id), '_blank');
                        }}
                      >
                        <TableCell className="font-medium">{req.brand_name}</TableCell>
                        <TableCell className="text-sm">{req.product_name}</TableCell>
                        <TableCell>
                          <BriefStatusBadge status={req.status} />
                        </TableCell>
                        <TableCell>
                          {req.status === 'ready' && (
                            <Button size="sm" variant="ghost" asChild>
                              <a href={getBriefUrl(req.id)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ChatBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm ${
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-foreground'
        }`}
      >
        {message.briefReady && message.briefId ? (
          <a
            href={getBriefUrl(message.briefId)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 font-medium underline underline-offset-2"
          >
            Brief ready! View Brief <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : (
          message.content
        )}
      </div>
    </div>
  );
}

function BriefStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'pending_data':
      return <Badge variant="secondary">Pending</Badge>;
    case 'generating':
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
          <Loader2 className="h-3 w-3 animate-spin mr-1" /> Generating
        </Badge>
      );
    case 'ready':
      return <Badge className="bg-green-100 text-green-800 border-green-300">Ready</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}
